/**
 * Shared knowledge base for Jerome Brown's agentic personal website.
 *
 * This module is consumed by BOTH:
 *   - the serverless function (api/chat.js) to ground the AI agent, and
 *   - the client-side fallback (src/services/agentClient.js) when the
 *     API is unavailable or no key is configured.
 *
 * Keep facts here in sync with the visible pages (LandingPage.vue,
 * ResumeTimeline.vue). It is intentionally CommonJS (.cjs) so Node
 * serverless functions can require it directly while webpack still
 * bundles it for the browser.
 */

const EMAIL = 'jerome@jeromejbrown.com';
const BLOG_URL = 'https://jeromejbrown.substack.com/';
const SITE_URL = 'https://jeromejbrown.com';

const PROFILE = {
  name: 'Jerome Brown',
  title: 'Product leader & builder',
  focus: 'fintech, marketplaces, and AI products',
  email: EMAIL,
  blog: BLOG_URL,
  site: SITE_URL,
  tagline: 'I turn complex problems into software people actually use.',
  summary:
    'From wealth management and construction tech to marketplaces and AI, Jerome has spent his career at the intersection of product strategy and hands-on execution — defining what to build, then building it.',
  narrative: [
    {
      title: 'Builder first',
      body:
        'He started as someone who needed to see ideas working, not just documented. He prototypes early, tests assumptions fast, and stays close to the code when it matters.',
    },
    {
      title: 'Systems thinker',
      body:
        'Good products sit inside systems: users, incentives, data, and operations. He maps those connections before committing to a direction, then reduces scope until what remains is sharp and shippable.',
    },
    {
      title: 'Beyond the desk',
      body:
        'Cooking, hiking, Italian, philosophy, and behavioral economics. He mentors people entering tech and believes the best work comes from people who are curious outside their job title.',
    },
  ],
};

const PROJECTS = [
  {
    name: 'Ritual Kitchen',
    icon: 'fas fa-utensils',
    color: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    role: 'Founder & Builder',
    description: 'Private chef booking platform connecting diners with culinary experiences.',
    stack: ['Next.js', 'Supabase', 'Stripe'],
    url: 'https://ritual.kitchen',
    isActive: true,
  },
  {
    name: 'Ritual Ops',
    icon: 'fas fa-people-group',
    color: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
    role: 'Founder & Builder',
    description: 'Work, reimagined. Empower teams with clarity and rhythm.',
    stack: ['Vue', 'Node.js'],
    url: 'https://www.ritualops.com/workforce',
    isActive: true,
  },
  {
    name: 'Wisconsin Workways',
    icon: 'fas fa-map-location-dot',
    color: 'linear-gradient(135deg, #1e6b9c, #267a55)',
    role: 'Founder & Builder',
    description: 'Interactive map and AI assistant for Wisconsin jobs, local economics, and the transit routes that connect people to work.',
    stack: ['Next.js', 'Mapbox', 'Gemini'],
    url: 'https://wisco-data.vercel.app/',
    isActive: true,
  },
  {
    name: 'Thryve Connect',
    icon: 'fas fa-people-arrows',
    color: 'linear-gradient(135deg, #ec4899, #be185d)',
    role: 'Founder & Builder',
    description: 'Connecting communities through seamless digital experiences.',
    stack: ['Vue', 'Node.js'],
    url: 'https://thryveconnect.com',
    isActive: true,
  },
];

const EXPERIENCES = [
  { company: 'Baird', role: 'Product Management', sector: 'Wealth management' },
  { company: 'Paradigm', role: 'Product Management', sector: 'Construction software', url: 'https://www.myparadigm.com' },
  { company: 'Landmark Credit Union', role: 'Product Management', sector: 'Financial services' },
  { company: 'EatStreet', role: 'Product Management · BizOps', sector: 'Marketplace' },
  { company: 'UW Credit Union', role: 'Business Development', sector: 'Financial services' },
  { company: 'BoostUp', role: 'Growth', sector: 'Sales enablement' },
  { company: 'ManpowerGroup', role: 'Innovation Consultant', sector: 'Workforce solutions' },
  { company: 'Rocket Mortgage', role: 'Client Relations', sector: 'Mortgage & fintech' },
];

const SKILLS = {
  product: [
    'Product strategy & roadmap ownership',
    '0→1 discovery, user research & validation',
    'PRD writing, user stories & acceptance criteria',
    'Wireframing & prototyping (Figma)',
    'Cross-functional team leadership',
    'Stakeholder alignment & executive communication',
    'Data-informed prioritization (RICE, ICE, OKRs)',
    'A/B testing, experimentation & funnel analysis',
    'Agile / Scrum delivery at startup and enterprise scale',
    'Go-to-market planning & launch coordination',
    'Competitive & market analysis',
    'API, platform & integration product design',
    'SEO, discoverability & organic growth',
    'Workflow automation (Zapier) & integrations',
    'AI agents, LLM workflows & copilot tooling',
    'Vendor evaluation, RFPs & build-vs-buy decisions',
  ],
  development: ['Vue.js', 'Angular', 'JavaScript', 'TypeScript', 'C#', '.NET', 'SQL', 'Azure', 'HTML/CSS', 'GitHub', 'Agile'],
  pmTools: ['Figma', 'Jira', 'Zapier', 'Salesforce', 'Dynamics 365', 'Power BI', 'Tableau', 'Mixpanel'],
  ai: ['OpenAI', 'MS Copilot', 'Cursor', 'Claude', 'Agents', 'AI / LLMs'],
};

/** Routes the navigate tool may serve visitors to. */
const VALID_PAGES = ['/about', '/contact', '/projects', '/teach'];

/**
 * How Jerome builds — his product-building expertise, grounded in the
 * narrative on /about and the products on the site.
 */
const BUILDING = {
  principles: [
    'Ship to learn — working prototypes beat decks and docs every time',
    'Scope ruthlessly — cut until the remaining slice is sharp and shippable',
    'Stay close to the code — product judgment compounds when you build what you decide',
    'Systems first — map users, incentives, data, and operations before committing',
    'Prototype the risky part first — kill the biggest assumption on day one',
  ],
  aiStack:
    'Cursor + Claude as a pair-building bench, OpenAI APIs for product features, agent workflows for ops automation, and lightweight evals before anything ships',
  playbook: [
    'Frame the problem in one sentence a stranger could repeat',
    'Sketch the thinnest end-to-end slice a real user could complete',
    'Build with AI pair-tools, test with 5 real users, iterate weekly',
    'Instrument what matters, then decide with data — not opinions',
  ],
};

/**
 * AI teaching curriculum — Beginner to Explorer. Powers /teach and the
 * start_lesson chat action so JB can teach visitors inside the chat.
 */
const TEACH_CURRICULUM = [
  {
    id: 'beginner',
    level: 'Beginner',
    tagline: 'From first prompt to daily habit',
    topics: [
      'What AI tools actually are (and are not)',
      'Writing prompts that get great answers',
      'Everyday wins: email, summaries, planning',
      'Spotting hallucinations & staying safe',
    ],
    outcome: 'Use AI confidently for everyday tasks — and know when not to trust it.',
  },
  {
    id: 'practitioner',
    level: 'Practitioner',
    tagline: 'From chatting to working',
    topics: [
      'Deep research & synthesis workflows',
      'Automating one repetitive task end-to-end',
      'Chained prompts & reusable templates',
      'Files, data, and context that make AI useful',
    ],
    outcome: 'Save hours a week with repeatable AI workflows you trust.',
  },
  {
    id: 'explorer',
    level: 'Explorer',
    tagline: 'From using AI to building with it',
    topics: [
      'How LLMs, tools & agents fit together',
      'Shipping your first AI-powered feature',
      'Evals: knowing your AI actually works',
      'Cost, latency & safety trade-offs',
    ],
    outcome: 'Build and ship your first AI agent or feature — with product judgment.',
  },
];

/**
 * Guided mini-lessons JB teaches inside the chat. `tryIt` is a prompt the
 * learner can paste into any AI tool to practice the skill immediately.
 */
const LESSONS = [
  {
    id: 'prompt-basics',
    level: 'beginner',
    title: 'Your first great prompt',
    minutes: 5,
    steps: [
      'Give the AI a role: "You are a patient writing coach."',
      'Add context: who you are and what you need in 1-2 sentences',
      'Say the format you want: bullet list, table, 3 options…',
      'Iterate: reply "make it shorter / simpler / more specific"',
    ],
    tryIt:
      "You are a patient writing coach. I'm [your role] and I need to [your task]. Give me 3 options in a bullet list, then ask me two questions to improve them.",
  },
  {
    id: 'ai-everyday',
    level: 'beginner',
    title: 'AI for everyday tasks',
    minutes: 6,
    steps: [
      'Pick one chore you dread: emails, planning, summarizing',
      'Paste the raw material plus your goal into the AI',
      'Ask for a draft, then edit it so it sounds like you',
      "Save the prompt that worked — that's your first template",
    ],
    tryIt:
      'Summarize this text in 3 bullet points for a busy executive, then list any action items: [paste text]',
  },
  {
    id: 'research-workflow',
    level: 'practitioner',
    title: 'A deep-research workflow you can trust',
    minutes: 8,
    steps: [
      'Start with questions, not a topic — write 5 you actually care about',
      'Have the AI draft a research outline, then challenge its gaps',
      'Ask for sources & counter-arguments on every key claim',
      'End with a one-page brief: findings, risks, recommendation',
    ],
    tryIt:
      "I'm researching [topic]. Ask me the 5 questions that would make your answer genuinely useful, then outline a one-page brief.",
  },
  {
    id: 'automate-a-task',
    level: 'practitioner',
    title: 'Automate one repetitive task',
    minutes: 10,
    steps: [
      'Write down the task as numbered steps — exactly as you do it',
      'Mark which steps follow rules (automatable) vs. judgment (yours)',
      'Have the AI draft the automation, then run it on real examples',
      'Add a human checkpoint before anything leaves your hands',
    ],
    tryIt:
      "Here's my repetitive task as steps: [paste steps]. Which steps can AI automate, which need my judgment, and what's the simplest automation to try first?",
  },
  {
    id: 'build-an-agent',
    level: 'explorer',
    title: 'Ship your first AI agent',
    minutes: 12,
    steps: [
      'Pick a job with clear inputs, tools, and a definition of done',
      'Give the agent a role, the tools it may use, and hard limits',
      'Wire one tool first (search, calculator, your API) — test it raw',
      'Add a loop: act → check result → retry or ask the human',
    ],
    tryIt:
      "Design a mini agent that [your job]. Give me: its role prompt, the 2-3 tools it needs, its limits, and how it should ask for help.",
  },
  {
    id: 'ai-product-mindset',
    level: 'explorer',
    title: 'From demo to product',
    minutes: 9,
    steps: [
      'Demos lie: test with your worst real-world input, not a clean one',
      'Define "good enough" numerically before you build evals',
      'Design the failure path: what the user sees when the AI is wrong',
      'Ship behind a flag, watch real usage, iterate weekly',
    ],
    tryIt:
      "I'm turning [AI capability] into a feature for [user]. What are the 5 ways it will fail in production and how should the UI handle each?",
  },
];

/** Find a lesson by loose id/title/level match (used by start_lesson). */
function findLesson(query) {
  if (!query || typeof query !== 'string') return null;
  const q = query.toLowerCase().trim();
  return (
    LESSONS.find((l) => l.id === q || l.title.toLowerCase() === q) ||
    LESSONS.find((l) => q.includes(l.id) || q.includes(l.title.toLowerCase())) ||
    LESSONS.find((l) => l.id.includes(q) || l.title.toLowerCase().includes(q)) ||
    LESSONS.find((l) => l.level === q) ||
    null
  );
}

/**
 * Build the system prompt that grounds the agent in this knowledge base.
 */
function buildSystemPrompt() {
  return [
    'You are "JB", the AI guide embedded in Jerome Brown\'s personal website (jeromejbrown.com).',
    'Your job is to help visitors learn about Jerome — his background, products, and skills — and connect interested people with him.',
    '',
    'GROUND RULES:',
    "- Ground every factual claim in the KNOWLEDGE BASE below. If something isn't covered (exact dates, compensation, private life), say you don't know and offer the contact route instead.",
    '- Never invent companies, job titles, dates, metrics, or endorsements.',
    "- You are Jerome's site assistant; refer to Jerome in the third person. Be warm, sharp, and concise: 2-5 sentences unless the visitor asks for depth.",
    '- Use tools proactively when they help: show_project when discussing one specific product, navigate to /about for deep-dive visitors, /projects for browsing, and open_contact whenever someone wants to hire, partner, or reach out.',
    '- After calling tools, continue your answer naturally and reference what the visitor can now see.',
    '- Format with short paragraphs and occasional **bold** for key names. No headings, no code blocks. For lists, use "- " at the start of each line (never "* ").',
    '',
    'KNOWLEDGE BASE:',
    '',
    `PROFILE: ${PROFILE.name} — ${PROFILE.title} focused on ${PROFILE.focus}.`,
    `Tagline: "${PROFILE.tagline}"`,
    `Summary: ${PROFILE.summary}`,
    ...PROFILE.narrative.map((block) => `${block.title}: ${block.body}`),
    '',
    `FEATURED PRODUCTS (${PROJECTS.length}):`,
    ...PROJECTS.map((p) => `- ${p.name} (${p.role}): ${p.description} Stack: ${p.stack.join(', ')}. Live at ${p.url}`),
    '',
    'EXPERIENCE (product, growth, and operations roles across industries):',
    ...EXPERIENCES.map((e) => `- ${e.company} — ${e.role} (${e.sector})`),
    '',
    'PRODUCT SKILLS:',
    SKILLS.product.map((s) => `- ${s}`).join('\n'),
    '',
    `TECHNICAL TOOLKIT — Development: ${SKILLS.development.join(', ')}. Product & analytics: ${SKILLS.pmTools.join(
      ', '
    )}. AI: ${SKILLS.ai.join(', ')}.`,
    '',
    'BUILDING EXPERTISE — how Jerome builds products:',
    ...BUILDING.principles.map((p) => `- ${p}`),
    `AI building stack: ${BUILDING.aiStack}.`,
    `Playbook: ${BUILDING.playbook.join(' → ')}`,
    '',
    'TEACHING (jerome teaches people to use AI on /teach and right here in chat):',
    ...TEACH_CURRICULUM.map(
      (c) => `- ${c.level} (${c.id}): ${c.tagline}. Topics: ${c.topics.join('; ')}. Outcome: ${c.outcome}`
    ),
    `Guided lessons (use start_lesson with the lesson id): ${LESSONS.map(
      (l) => `${l.id} [${l.level}, ${l.minutes} min] "${l.title}"`
    ).join('; ')}`,
    '',
    'TEACHING RULES: When a visitor wants to learn AI — from absolute beginner to explorer — teach patiently at their level, one concept at a time. Offer start_lesson to begin a guided lesson; use show_curriculum to show the full path. When they ask how Jerome builds, share the BUILDING principles. When they want to send Jerome a message, use contact_form so they never have to leave the chat.',
    '',
    `CONTACT: email ${EMAIL}, blog ${BLOG_URL}, contact form at ${SITE_URL}/contact — or use the contact_form tool to let them message Jerome right inside the chat.`,
  ].join('\n');
}

/**
 * Loose-match a project by name for the show_project tool.
 */
function findProject(name) {
  if (!name || typeof name !== 'string') return null;
  const query = name.toLowerCase().trim();
  return (
    PROJECTS.find((p) => p.name.toLowerCase() === query) ||
    PROJECTS.find((p) => p.name.toLowerCase().includes(query)) ||
    PROJECTS.find((p) => query.includes(p.name.toLowerCase())) ||
    null
  );
}

const FALLBACK_OVERVIEW =
  "I'm JB, Jerome's AI guide. I can walk you through his **featured products**, his **career experience**, his " +
  '**tech stack**, or help you **get in touch** with him.\n\nWhat would you like to explore?';

/**
 * Keyword-driven offline answers. Used when no AI key is configured or the
 * function cannot be reached, so the chat always works.
 */
function getFallbackAnswer(messages) {
  const lastUser = [...(messages || [])].reverse().find((m) => m && m.role === 'user');
  const text = String((lastUser && lastUser.content) || '').toLowerCase();
  const has = (...words) => words.some((w) => text.includes(w));

  // Teaching intents — checked first so "start the build-an-agent lesson"
  // isn't swallowed by the projects branch below.
  if (has('teach me', 'lesson', 'curriculum', 'start the', 'learn ai', 'teach', 'beginner to')) {
    const lesson = findLesson(text) || LESSONS.find((l) => l.level === 'beginner');
    return {
      reply:
        `Let's do this. **${lesson.title}** (${lesson.level}, ~${lesson.minutes} min) — right here, step by step:\n\n` +
        lesson.steps.map((s, i) => `${i + 1}. ${s}`).join('\n') +
        '\n\nWhen you\'re done, paste the **Try it** prompt below into any AI tool — that\'s the reps that build the skill.',
      actions: [{ type: 'start_lesson', lesson }],
    };
  }

  if (has('how to use ai', 'where do i start', 'learning path', 'levels')) {
    return {
      reply:
        "Jerome teaches AI in three levels — and I can guide you through all of it right here:\n\n" +
        TEACH_CURRICULUM.map((c) => `- **${c.level}** — ${c.tagline}`).join('\n') +
        "\n\nThe full path lives on the Teach page, or just tell me your level and I'll start a lesson.",
      actions: [{ type: 'show_curriculum' }],
    };
  }

  if (has('expertise', 'playbook', 'principle', 'approach', 'philosophy')) {
    return {
      reply:
        "Jerome's building philosophy, in his own terms:\n\n" +
        BUILDING.principles.map((p) => `- **${p.split(' — ')[0]}** — ${p.split(' — ')[1] || ''}`).join('\n') +
        `\n\nHis AI stack: ${BUILDING.aiStack}.`,
      actions: [],
    };
  }

  if (has('project', 'built', 'build', 'product', 'portfolio', 'startup')) {
    return {
      reply:
        'Jerome ships his own ideas, not just slides. His featured products:\n\n' +
        PROJECTS.map((p) => `- **${p.name}** — ${p.description}`).join('\n') +
        "\n\nI've pulled the cards up below — each one is live and visitable.",
      actions: PROJECTS.map((project) => ({ type: 'show_project', project })),
    };
  }

  if (has('experience', 'career', 'job', 'work', 'background', 'company', 'employ', 'resume', 'industry')) {
    return {
      reply:
        'Jerome has led product across fintech, marketplaces, construction software, and workforce solutions — including ' +
        '**Baird** (wealth management), **Paradigm** (construction software), **Landmark Credit Union**, **EatStreet** ' +
        '(marketplace), **Rocket Mortgage**, **BoostUp**, **ManpowerGroup**, and **UW Credit Union**.\n\n' +
        'The common thread: defining what to build, then helping ship it. The full timeline lives on the About page.',
      actions: [{ type: 'navigate', page: '/about' }],
    };
  }

  if (has('skill', 'stack', 'tech', 'tool', 'language', 'framework', 'code', 'program')) {
    return {
      reply:
        'Jerome pairs product craft with hands-on building:\n\n' +
        '- **Development:** Vue.js, Angular, JavaScript, TypeScript, C#, .NET, SQL, Azure\n' +
        '- **Product & analytics:** Figma, Jira, Salesforce, Power BI, Tableau, Mixpanel\n' +
        '- **AI:** LLM workflows, agents, copilot tooling (OpenAI, Claude, Cursor)\n\n' +
        'Strategy on the whiteboard, working code in the repo.',
      actions: [],
    };
  }

  if (has('contact', 'email', 'hire', 'reach', 'touch', 'talk', 'connect', 'freelance', 'available', 'work with')) {
    return {
      reply:
        `You can email **${EMAIL}** — or skip the page entirely and write right here. I'll pass it straight to Jerome.`,
      actions: [{ type: 'contact_form' }],
    };
  }

  if (has('who', 'about', 'jerome', 'intro', 'rundown', 'yourself')) {
    return {
      reply:
        `**${PROFILE.name}** — ${PROFILE.title.toLowerCase()} focused on ${PROFILE.focus}. ${PROFILE.summary}\n\n` +
        'He prototypes early, thinks in systems, and mentors people breaking into tech. Ask me about his products, career, or how to reach him.',
      actions: [],
    };
  }

  return { reply: FALLBACK_OVERVIEW, actions: [] };
}

const FOLLOW_UPS = {
  teach: ['Show the full curriculum', 'Next lesson please', 'Show featured projects'],
  building: ['Show his tech stack', 'Show featured projects', 'Get in touch'],
  projects: ['What powers Ritual Ops?', 'Which was hardest to ship?', 'Show me another one'],
  experience: ['What are his strongest product skills?', "What does 'builder first' mean to him?", 'Open his About page'],
  skills: ['Show a project that uses them', 'How does he use AI in his workflow?', 'Open his About page'],
  contact: ['What kind of roles fit him?', 'Does he take partnerships?', 'Email him now'],
  about: ['Show his featured projects', 'Walk through his career', 'Get in touch'],
  default: ['Featured projects', 'Career experience', 'Tech stack'],
};

/**
 * Deterministic next-question suggestions shown under each answer.
 * Keeps visitors moving without extra model calls; mirrors the fallback
 * keyword matcher.
 */
function getSuggestedFollowUps(messages) {
  const lastUser = [...(messages || [])].reverse().find((m) => m && m.role === 'user');
  const text = String((lastUser && lastUser.content) || '').toLowerCase();
  const has = (...words) => words.some((w) => text.includes(w));

  if (has('teach', 'lesson', 'curriculum', 'learn', 'beginner', 'prompt', 'chatgpt')) {
    return FOLLOW_UPS.teach;
  }
  if (has('expertise', 'playbook', 'principle', 'approach', 'philosophy')) {
    return FOLLOW_UPS.building;
  }
  if (has('project', 'built', 'build', 'product', 'portfolio', 'startup')) return FOLLOW_UPS.projects;
  if (has('experience', 'career', 'job', 'work', 'background', 'company', 'employ', 'resume')) {
    return FOLLOW_UPS.experience;
  }
  if (has('skill', 'stack', 'tech', 'tool', 'language', 'framework', 'ai ', 'llm', 'agent')) {
    return FOLLOW_UPS.skills;
  }
  if (has('contact', 'email', 'hire', 'reach', 'touch', 'talk', 'connect', 'role', 'hiring', 'partnership')) {
    return FOLLOW_UPS.contact;
  }
  if (has('who', 'about', 'jerome', 'intro', 'rundown', 'yourself', 'outside', 'write', 'blog')) {
    return FOLLOW_UPS.about;
  }
  return FOLLOW_UPS.default;
}

module.exports = {
  EMAIL,
  BLOG_URL,
  SITE_URL,
  PROFILE,
  PROJECTS,
  EXPERIENCES,
  SKILLS,
  BUILDING,
  TEACH_CURRICULUM,
  LESSONS,
  VALID_PAGES,
  buildSystemPrompt,
  findProject,
  findLesson,
  getFallbackAnswer,
  getSuggestedFollowUps,
};
